import { AttackPattern, PatternState } from '../AttackPattern.js';
import { ARENA } from '../../../../config/constants.js';

const CELLS = 7; // vertical slots per wall
const WAVES = 4;
const WAVE_INTERVAL = 0.32;

/**
 * Bullet Board — a Deltarune-style structured pattern: a vertical wall of
 * bullets with exactly one safe gap sweeps across the arena. Several waves
 * fire in sequence, each with the gap in a different slot, so the player
 * has to actually relocate between waves rather than find one spot and
 * stand in it. Unlocks at phase 2 (50%HP) by default.
 *
 * `config` (damage/speed) is per-boss tuning, supplied by data/bosses.js.
 */
export class BulletBoardGrid extends AttackPattern {
  constructor({ minPhase = 2, config }) {
    super({ name: 'Bullet Board', telegraphDuration: 0.6, activeDuration: 1.8, minPhase });
    this.config = config;
  }

  onBegin() {
    this._dir = Math.random() < 0.5 ? 1 : -1;
    this._wavesFired = 0;
    this._waveTimer = 0;
    // Decide the gaps during the telegraph so the safe lane is a readable
    // promise, not a reaction test after the wall is already moving.
    this._gapCells = Array.from({ length: WAVES }, () => Math.floor(Math.random() * CELLS));
  }

  onActiveTick(dt, boss, player, pool) {
    this._waveTimer -= dt;
    if (this._waveTimer <= 0 && this._wavesFired < WAVES) {
      this._fireWall(pool);
      this._wavesFired++;
      this._waveTimer = WAVE_INTERVAL;
    }
  }

  _fireWall(pool) {
    const { damage, speed } = this.config;
    const top = ARENA.y + 8;
    const spacing = (ARENA.height - 16) / (CELLS - 1);
    const gapCell = this._gapCells[this._wavesFired] ?? 0;
    const startX = this._dir > 0 ? ARENA.x - 10 : ARENA.x + ARENA.width + 10;

    for (let i = 0; i < CELLS; i++) {
      if (i === gapCell) continue; // the one safe lane in this wave
      pool.fire({
        x: startX,
        y: top + spacing * i,
        vx: this._dir * speed,
        vy: 0,
        radius: 3,
        damage,
        color: '#8ad1ff',
        lifetime: 6,
      });
    }
  }

  getVisual(boss) {
    if (this.state !== PatternState.TELEGRAPH) return null;
    return {
      kind: 'boardPreview',
      x: boss.x,
      direction: this._dir,
      cells: CELLS,
      gapCells: this._gapCells,
      arena: { x: ARENA.x, y: ARENA.y, width: ARENA.width, height: ARENA.height },
    };
  }
}
